import { useCallback, useRef, useState } from "react";

const CHUNK_SIZE = 5_000;

export type ProgressiveFetchProgress = {
  status: "idle" | "fetching" | "done" | "error";
  loaded: number;
  total: number | null;
};

export type ChunkResponse<T> = {
  items: T[];
  total: number;
};

export const useProgressiveFetch = <T>(fetchChunk: (offset: number, limit: number) => Promise<ChunkResponse<T>>) => {
  const [progress, setProgress] = useState<ProgressiveFetchProgress>({ status: "idle", loaded: 0, total: null });
  const abortRef = useRef<symbol>(Symbol("idle"));

  const start = useCallback(async (): Promise<T[] | null> => {
    const token = Symbol("progressive-fetch");
    abortRef.current = token;
    setProgress({ status: "fetching", loaded: 0, total: null });

    const isCurrent = () => abortRef.current === token;

    try {
      const firstChunk = await fetchChunk(0, CHUNK_SIZE);
      if (!isCurrent()) return null;

      const total = firstChunk.total;
      const items = [...firstChunk.items];

      if (items.length >= total || firstChunk.items.length < CHUNK_SIZE) {
        setProgress({ status: "done", loaded: items.length, total });
        return items;
      }
      setProgress({ status: "fetching", loaded: items.length, total });

      let offset = CHUNK_SIZE;
      while (offset < total) {
        const chunk = await fetchChunk(offset, CHUNK_SIZE);
        if (!isCurrent()) return null;

        items.push(...chunk.items);
        offset += CHUNK_SIZE;

        const loaded = Math.min(items.length, total);
        const isDone = loaded >= total || chunk.items.length < CHUNK_SIZE;
        setProgress({ status: isDone ? "done" : "fetching", loaded, total });
        if (isDone) break;
      }

      return items;
    } catch {
      if (isCurrent()) {
        setProgress((prev) => ({ ...prev, status: "error" }));
      }
      return null;
    }
  }, [fetchChunk]);

  const reset = useCallback(() => {
    abortRef.current = Symbol("reset");
    setProgress({ status: "idle", loaded: 0, total: null });
  }, []);

  return { progress, start, reset };
};
